import { Component } from 'react';
import sharedStyles from '../single-page.module.scss';
import { genStyles } from '../converter-layout/generator-layout';
import styles from './text-case-converter.component.module.scss';

interface CaseState {
    text: string;
    activeCase: string | null;
    copiedField: string | null;
}

type CaseFormat = { id: string; label: string; preview: string; fn: (s: string) => string };

const toSentence = (s: string) => s.replace(/(^\s*\w|[.!?]\s+\w)/g, m => m.toUpperCase()).replace(/^./, m => m.toUpperCase());
const toTitle = (s: string) => s.replace(/\b\w/g, m => m.toUpperCase());
const toCamel = (s: string) => s.replace(/[^a-zA-Z0-9]+(.)/g, (_, c) => c.toUpperCase()).replace(/^./, m => m.toLowerCase());
const toSnake = (s: string) => s.trim().replace(/[\s\-]+/g, '_').replace(/([a-z])([A-Z])/g, '$1_$2').toLowerCase();
const toKebab = (s: string) => s.trim().replace(/[\s_]+/g, '-').replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase();
const toPascal = (s: string) => { const c = toCamel(s); return c.charAt(0).toUpperCase() + c.slice(1); };
const toConstant = (s: string) => toSnake(s).toUpperCase();

const FORMATS: CaseFormat[] = [
    { id: 'sentence', label: 'Sentence', preview: 'Aa', fn: toSentence },
    { id: 'lower', label: 'lower', preview: 'aa', fn: s => s.toLowerCase() },
    { id: 'upper', label: 'UPPER', preview: 'AA', fn: s => s.toUpperCase() },
    { id: 'title', label: 'Title', preview: 'Aa Bb', fn: toTitle },
    { id: 'camel', label: 'camel', preview: 'aaBb', fn: toCamel },
    { id: 'snake', label: 'snake', preview: 'aa_bb', fn: toSnake },
    { id: 'kebab', label: 'kebab', preview: 'aa-bb', fn: toKebab },
    { id: 'pascal', label: 'Pascal', preview: 'AaBb', fn: toPascal },
    { id: 'constant', label: 'CONSTANT', preview: 'AA_BB', fn: toConstant },
];

export default class TextCaseConverterComponent extends Component<{}, CaseState> {
    private originalText = '';
    private copyTimeout: ReturnType<typeof setTimeout> | null = null;

    state: CaseState = { text: '', activeCase: null, copiedField: null };

    handleInput = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        this.originalText = e.target.value;
        this.setState({ text: e.target.value, activeCase: null });
    };

    applyCase = (format: CaseFormat) => {
        const source = this.originalText || this.state.text;
        if (!source.trim()) return;
        this.setState({ text: format.fn(source), activeCase: format.id });
    };

    copyText = () => {
        navigator.clipboard.writeText(this.state.text).then(() => {
            if (this.copyTimeout) clearTimeout(this.copyTimeout);
            this.setState({ copiedField: 'text' });
            this.copyTimeout = setTimeout(() => this.setState({ copiedField: null }), 2000);
        });
    };

    clear = () => {
        this.originalText = '';
        this.setState({ text: '', activeCase: null, copiedField: null });
    };

    render() {
        const { text, activeCase, copiedField } = this.state;
        const wordCount = text.trim() ? text.trim().split(/\s+/).length : 0;
        const charCount = text.length;
        const hasText = text.trim().length > 0;
        const isCopied = copiedField === 'text';

        return (
            <div className="row">
                <div className="col-12">
                    <div className={genStyles.section}>
                        <div className={styles.header}>
                            <label className={sharedStyles.label} htmlFor="case-input">Your Text</label>
                            <button className={sharedStyles.copyButton} onClick={this.clear}>✕ Clear</button>
                        </div>
                        <textarea
                            id="case-input"
                            className={sharedStyles.textarea}
                            placeholder="Paste or type your text here..."
                            value={text}
                            onChange={this.handleInput}
                            rows={8}
                        />
                    </div>
                </div>

                <div className="col-12">
                    <div className={genStyles.section}>
                        <div className={styles.buttons}>
                            {FORMATS.map(f => (
                                <button
                                    key={f.id}
                                    className={`${styles.caseButton} ${activeCase === f.id ? styles.active : ''} ${!hasText ? styles.disabled : ''}`}
                                    onClick={() => this.applyCase(f)}
                                    disabled={!hasText}
                                    title={f.label}
                                >
                                    <span className={styles.preview}>{f.preview}</span>
                                    <span className={styles.caseLabel}>{f.label}</span>
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="col-12">
                    <div className={genStyles.section}>
                        <div className={sharedStyles.stats}>
                            <span>Words: <strong>{wordCount}</strong></span>
                            <span>Characters: <strong>{charCount}</strong></span>
                            <button
                                className={`${sharedStyles.copyButton} ${isCopied ? sharedStyles.copyButtonSuccess : ''}`}
                                onClick={this.copyText}
                                disabled={!hasText}
                            >
                                {isCopied ? '✓ Copied' : 'Copy Text'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}
